import { useMemo, useCallback } from 'react';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useTranslation } from '@packlink/translation-provider';
import { Currency, ServiceRepository, PointType } from '@packlink/packlink-sdk';
import { ServiceDetails } from './ServiceDetails.js';
import { jsx, Fragment } from '@emotion/react/jsx-runtime';
import { checkIconName, getCarrierLogo } from '../utils/Icon.js';
import { ServiceListEvents } from '../types/types.js';

const formatPercent = (input, locales, minDigits) => {
  const formatter = new Intl.NumberFormat(locales, {
    maximumFractionDigits: 2,
    minimumFractionDigits: minDigits !== null && minDigits !== void 0 ? minDigits : 2,
    style: 'percent'
  });
  return formatter.format(input);
};
const mapServiceToServiceCard = function (service, cdnUrl, t, showVat, priceAnatomy) {
  var _service$availableDat, _service$availableDat2, _service$dropOff, _service$dropOff2, _service$timeLeftForF, _service$timeLeftForF2, _service$serviceInfo, _service$price, _service$price$taxes, _service$products$por, _service$price2;
  let locales = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : [];
  let tagsFeatureEnabled = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  const from = {
    date: (_service$availableDat = service.availableDates) === null || _service$availableDat === void 0 ? void 0 : (_service$availableDat2 = _service$availableDat[0]) === null || _service$availableDat2 === void 0 ? void 0 : _service$availableDat2.date,
    dropOff: (_service$dropOff = service.dropOff) === null || _service$dropOff === void 0 ? void 0 : _service$dropOff.origin
  };
  const to = {
    date: service.firstEstimatedDeliveryDate,
    dropOff: (_service$dropOff2 = service.dropOff) === null || _service$dropOff2 === void 0 ? void 0 : _service$dropOff2.destination
  };
  const timeLeftToBook = {
    type: (_service$timeLeftForF = service.timeLeftForFirstAvailableDate) === null || _service$timeLeftForF === void 0 ? void 0 : _service$timeLeftForF.unit,
    value: (_service$timeLeftForF2 = service.timeLeftForFirstAvailableDate) === null || _service$timeLeftForF2 === void 0 ? void 0 : _service$timeLeftForF2.value
  };
  const transit = {
    duration: service.transitHours,
    guaranteed: false
  };
  const serviceInfo = (_service$serviceInfo = service.serviceInfo) === null || _service$serviceInfo === void 0 ? void 0 : _service$serviceInfo.map(info => {
    const {
      icon,
      text: key
    } = info;
    return {
      icon: checkIconName(icon),
      key
    };
  });
  const appliedTax = showVat && (_service$price = service.price) !== null && _service$price !== void 0 && (_service$price$taxes = _service$price.taxes) !== null && _service$price$taxes !== void 0 && _service$price$taxes.length ? formatPercent((service.price.taxes[0].percentage || 0) / 100, locales, 0) : undefined;
  return {
    carrierName: service.carrierName,
    fallbackLogo: getCarrierLogo(cdnUrl, 'default'),
    from,
    logo: getCarrierLogo(cdnUrl, service.logoId),
    price: Currency.format(((_service$products$por = service.products.porterage) === null || _service$products$por === void 0 ? void 0 : _service$products$por.basePrice) || 0, service.currency, false, locales[0]),
    appliedTax,
    serviceId: service.id,
    serviceInfo,
    serviceName: service.name,
    timeLeftToBook,
    to,
    transit,
    isOwnContract: service.isOwnContract,
    priceAnatomyItems: mapPriceAnatomyToServiceCard(service.id, priceAnatomy),
    tags: service.tags,
    tagsFeatureEnabled,
    taxPrice: (_service$price2 = service.price) === null || _service$price2 === void 0 ? void 0 : _service$price2.taxPrice,
    products: service.products
  };
};
const mapDropOffToServiceCard = function () {
  let dropOffPoints = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return dropOffPoints.reduce((acc, dropOff) => {
    var _dropOff$id, _dropOff$openingTimes;
    dropOff.sortOpeningTimes();
    const dropOffPoint = {
      ...dropOff.toJSON(),
      id: (_dropOff$id = dropOff.id) === null || _dropOff$id === void 0 ? void 0 : _dropOff$id.toString(),
      openingTimes: (_dropOff$openingTimes = dropOff.openingTimes) === null || _dropOff$openingTimes === void 0 ? void 0 : _dropOff$openingTimes.map(openingTime => ({
        day: 'drop-off.address.weekday.' + openingTime.day,
        time: openingTime.time
      })),
      zip: dropOff.zipCode
    };
    acc.push(dropOffPoint);
    return acc;
  }, []);
};
const mapPriceAnatomyToServiceCard = (serviceId, priceAnatomy) => {
  if (!priceAnatomy) {
    return undefined;
  }
  const conceptsSlugs = {
    carrier_service: 'Rate',
    margin: 'Margin',
    management_fee: 'Management Fee',
    additional_handling: 'Additional Handling'
  };
  const additionalData = [{
    key: 'Service ID',
    value: `${serviceId}`
  }];
  const mappedPriceAnatomyItems = priceAnatomy.items.filter(priceAnatomyItem => !!priceAnatomyItem.basePrice && !!priceAnatomyItem.concept).map(_ref => {
    let {
      concept,
      basePrice,
      extraData,
      currency
    } = _ref;
    const formatedBasePrice = Currency.format(basePrice, currency);
    const percentage = extraData !== null && extraData !== void 0 && extraData.percentage ? (parseFloat(extraData.percentage) * 100).toFixed(2) : null;
    const value = percentage ? `${percentage}% (${formatedBasePrice})` : formatedBasePrice;
    return {
      key: conceptsSlugs[concept],
      value
    };
  });
  return [...additionalData, ...mappedPriceAnatomyItems];
};
const ServiceItem = _ref2 => {
  let {
    from,
    service,
    ServiceDetails: ServiceDetails$1,
    cdnUrl,
    index,
    showVat,
    onSelect,
    onDetailsRequest,
    onError,
    onLoading,
    children,
    onDropOffPointsRequest,
    priceAnatomy,
    tagsFeatureEnabled,
    ...rest
  } = _ref2;
  const sdk = useSdk();
  const {
    t,
    i18n
  } = useTranslation();
  const serviceRepository = useMemo(() => new ServiceRepository(sdk.apiClient), [sdk.apiClient]);
  const serviceCardProps = useMemo(() => mapServiceToServiceCard(service, cdnUrl, t, showVat, priceAnatomy, [...i18n.languages], tagsFeatureEnabled), [service, cdnUrl, t, showVat, priceAnatomy, i18n.languages, tagsFeatureEnabled]);
  const select = useCallback(() => {
    onSelect(service, index);
  }, [index, onSelect, service]);
  const requestDetails = useCallback(async () => {
    try {
      onLoading && onLoading(ServiceListEvents.DETAILS, true);
      const details = await serviceRepository.getDetails(service);
      onDetailsRequest && onDetailsRequest(service, details, index);
      const DetailsComp = ServiceDetails$1 || ServiceDetails;
      return details && jsx(DetailsComp, {
        serviceDetails: details
      });
    } catch (e) {
      const error = e;
      if (error.status && error.status < 500) {
        error.log();
      }
      onError && onError(ServiceListEvents.DETAILS, error);
    } finally {
      onLoading && onLoading(ServiceListEvents.DETAILS, false);
    }
    return null;
  }, [onLoading, serviceRepository, service, onDetailsRequest, index, ServiceDetails$1, onError]);
  const requestDropOff = useCallback(async () => {
    if (!from) {
      return [];
    }
    try {
      onLoading && onLoading(ServiceListEvents.DROPOFF, true);
      const dropOffPoints = await sdk.v1.dropOffs.all({
        city: from.city,
        code: from.alpha2Code,
        serviceId: service.id.toString(),
        zip: from.zipCode,
        pointsType: PointType.DROP_OFF
      });
      const dropOffPointsServiceCard = mapDropOffToServiceCard(dropOffPoints);
      onDropOffPointsRequest && onDropOffPointsRequest(service, dropOffPointsServiceCard, index);
      return dropOffPointsServiceCard;
    } catch (e) {
      const error = e;
      if (error.status && error.status < 500) {
        error.log();
      }
      onError && onError(ServiceListEvents.DROPOFF, error);
    } finally {
      onLoading && onLoading(ServiceListEvents.DROPOFF, false);
    }
    return [];
  }, [onDropOffPointsRequest, from, onLoading, sdk.v1.dropOffs, service, index, onError]);
  return jsx(Fragment, {
    children: children({
      ...serviceCardProps,
      service,
      onSelect: select,
      onDetailsRequest: requestDetails,
      onDropOffPointsRequest: requestDropOff,
      ...rest
    })
  });
};

export { ServiceItem, mapServiceToServiceCard };
//# sourceMappingURL=ServiceItem.js.map
